import { useState } from "react";
import { toast } from "sonner";
import { Warehouse, Boxes, AlertTriangle, Building2, ScanLine, PackagePlus, ArrowLeftRight, History, Search } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { QrScanner } from "@/components/QrScanner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

function apiErr(e, fb) { return e?.response?.data?.detail || fb; }
function fmt(t) { return t ? new Date(t).toLocaleString("en-ZA", { timeStyle: "short", dateStyle: "short" }) : "—"; }

// Barcode/QR-enabled SKU input
function BarcodeInput({ value, onChange, testid }) {
  const [scan, setScan] = useState(false);
  return (
    <div>
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} placeholder="EBK-SKU-1001" data-testid={testid} className="font-mono" />
        <Button type="button" variant="outline" onClick={() => setScan((s) => !s)} data-testid={`${testid}-scan-toggle`} title="Scan barcode"><ScanLine size={16} /></Button>
      </div>
      {scan && (
        <div className="mt-3 rounded-lg border border-border p-3 bg-muted/30">
          <QrScanner onResult={(t) => { onChange(t.trim().toUpperCase()); setScan(false); toast.success(`Scanned ${t.trim().toUpperCase()}`); }} />
        </div>
      )}
    </div>
  );
}

function WarehouseSelect({ value, onChange, names, testid, placeholder = "Select warehouse", allowCustom }) {
  const [custom, setCustom] = useState(false);
  if (custom || (allowCustom && names.length === 0)) {
    return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Warehouse name" data-testid={testid} />;
  }
  return (
    <Select value={value} onValueChange={(v) => { if (v === "__new__") { setCustom(true); onChange(""); } else onChange(v); }}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
        {allowCustom && <SelectItem value="__new__">+ New warehouse…</SelectItem>}
      </SelectContent>
    </Select>
  );
}

// -------- Scan / Lookup --------
function ScanDialog({ names, onChanged }) {
  const [open, setOpen] = useState(false);
  const [sku, setSku] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const lookup = async (code) => {
    const q = (code || sku).trim().toUpperCase();
    if (!q) return;
    setLoading(true); setResult(null);
    try { const r = await api.get(`/warehouse/lookup/${encodeURIComponent(q)}`); setResult(r.data); }
    catch (e) { setResult(null); toast.error(apiErr(e, "SKU not found")); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setResult(null); setSku(""); } }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="wh-open-scan"><Search size={16} className="mr-2" /> Scan / Lookup</Button></DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="wh-scan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine size={18} className="text-primary" /> Scan a barcode</DialogTitle><DialogDescription>Scan or type a SKU to see where it is stored and how much is on hand.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Label>SKU / barcode</Label>
          <BarcodeInput value={sku} onChange={(v) => setSku(v)} testid="wh-scan-sku" />
          <Button onClick={() => lookup()} disabled={loading} className="w-full bg-primary hover:bg-red-700" data-testid="wh-scan-go">{loading ? "Looking up…" : "Look up stock"}</Button>
          {result && (
            <div className="rounded-lg border border-border p-4" data-testid="wh-scan-result">
              <p className="font-heading font-bold">{result.name} <span className="font-mono text-sm text-muted-foreground">{result.sku}</span></p>
              <p className="text-sm text-muted-foreground mb-2">Total on hand: <b className="text-foreground">{result.total_quantity}</b></p>
              <div className="space-y-1.5">
                {result.locations.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm rounded-md bg-muted/40 px-3 py-2">
                    <span>{l.warehouse} · <span className="font-mono">{l.location_bin}</span></span>
                    <span className="font-semibold">{l.quantity} {l.uom}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Put-Away --------
function PutAwayDialog({ names, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ sku: "", warehouse: "", location_bin: "", quantity: "", name: "", uom: "Unit" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.sku || !f.warehouse || !f.location_bin || !f.quantity) return toast.error("SKU, warehouse, bin and quantity are required");
    setBusy(true);
    try {
      await api.post("/warehouse/putaway", { ...f, quantity: parseInt(f.quantity, 10) });
      toast.success(`Put away ${f.quantity} × ${f.sku}`);
      setOpen(false); setF({ sku: "", warehouse: "", location_bin: "", quantity: "", name: "", uom: "Unit" });
      onChanged();
    } catch (e) { toast.error(apiErr(e, "Put-away failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-red-700" data-testid="wh-open-putaway"><PackagePlus size={16} className="mr-2" /> Put Away</Button></DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="wh-putaway-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PackagePlus size={18} className="text-primary" /> Receive & put away stock</DialogTitle><DialogDescription>Book received cargo into a warehouse bin. Adds to existing stock or creates a new SKU.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>SKU / barcode</Label><BarcodeInput value={f.sku} onChange={(v) => setF({ ...f, sku: v })} testid="wh-putaway-sku" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Warehouse</Label><WarehouseSelect value={f.warehouse} onChange={(v) => setF({ ...f, warehouse: v })} names={names} testid="wh-putaway-warehouse" allowCustom /></div>
            <div><Label>Bin location</Label><Input value={f.location_bin} onChange={set("location_bin")} placeholder="A-04-2" data-testid="wh-putaway-bin" className="font-mono" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Quantity</Label><Input type="number" min="1" value={f.quantity} onChange={set("quantity")} placeholder="120" data-testid="wh-putaway-qty" /></div>
            <div><Label>Unit</Label><Input value={f.uom} onChange={set("uom")} placeholder="Pallet" data-testid="wh-putaway-uom" /></div>
          </div>
          <div><Label>Product name <span className="text-muted-foreground text-xs">(for new SKUs)</span></Label><Input value={f.name} onChange={set("name")} placeholder="Pallet - Canned Goods" data-testid="wh-putaway-name" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-primary hover:bg-red-700 w-full" data-testid="wh-putaway-submit">{busy ? "Saving…" : "Confirm Put-Away"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Transfer --------
function TransferDialog({ names, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ sku: "", from_warehouse: "", to_warehouse: "", to_bin: "", quantity: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.sku || !f.from_warehouse || !f.to_warehouse || !f.to_bin || !f.quantity) return toast.error("All fields are required");
    setBusy(true);
    try {
      await api.post("/warehouse/transfer", { ...f, quantity: parseInt(f.quantity, 10) });
      toast.success(`Transferred ${f.quantity} × ${f.sku}`);
      setOpen(false); setF({ sku: "", from_warehouse: "", to_warehouse: "", to_bin: "", quantity: "" });
      onChanged();
    } catch (e) { toast.error(apiErr(e, "Transfer failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="wh-open-transfer"><ArrowLeftRight size={16} className="mr-2" /> Transfer</Button></DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="wh-transfer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowLeftRight size={18} className="text-primary" /> Stock transfer</DialogTitle><DialogDescription>Move stock of a SKU from one warehouse and bin to another.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>SKU / barcode</Label><BarcodeInput value={f.sku} onChange={(v) => setF({ ...f, sku: v })} testid="wh-transfer-sku" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From warehouse</Label><WarehouseSelect value={f.from_warehouse} onChange={(v) => setF({ ...f, from_warehouse: v })} names={names} testid="wh-transfer-from" /></div>
            <div><Label>To warehouse</Label><WarehouseSelect value={f.to_warehouse} onChange={(v) => setF({ ...f, to_warehouse: v })} names={names} testid="wh-transfer-to" allowCustom /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Destination bin</Label><Input value={f.to_bin} onChange={set("to_bin")} placeholder="B-02-1" data-testid="wh-transfer-bin" className="font-mono" /></div>
            <div><Label>Quantity</Label><Input type="number" min="1" value={f.quantity} onChange={set("quantity")} placeholder="20" data-testid="wh-transfer-qty" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-primary hover:bg-red-700 w-full" data-testid="wh-transfer-submit">{busy ? "Moving…" : "Confirm Transfer"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TYPE_BADGE = {
  putaway: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15",
  transfer: "bg-blue-100 text-blue-700 dark:bg-blue-500/15",
};

export default function WarehouseOps() {
  const { data, loading, setData } = useFetch(() => api.get("/warehouse").then((r) => r.data));
  const moves = useFetch(() => api.get("/warehouse/movements").then((r) => r.data));
  if (loading) return <Spinner />;
  const s = data.summary;
  const names = data.warehouse_names || [];

  const refresh = async () => {
    setData((await api.get("/warehouse")).data);
    moves.setData((await api.get("/warehouse/movements")).data);
  };

  return (
    <div>
      <PageHeader title="Warehouse Management" subtitle="Barcode receiving · put-away · transfers · ERP-ready">
        <div className="flex flex-wrap gap-2">
          <ScanDialog names={names} onChanged={refresh} />
          <PutAwayDialog names={names} onChanged={refresh} />
          <TransferDialog names={names} onChanged={refresh} />
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="wh-skus" label="Total SKUs" value={s.total_skus} icon={Boxes} />
        <StatCard testid="wh-units" label="Total Units" value={s.total_units.toLocaleString()} icon={Warehouse} accent="text-blue-600" />
        <StatCard testid="wh-low" label="Low Stock" value={s.low_stock} icon={AlertTriangle} accent="text-orange-600" />
        <StatCard testid="wh-houses" label="Warehouses" value={s.warehouses} icon={Building2} accent="text-emerald-600" />
      </div>

      <Tabs defaultValue="inventory">
        <TabsList>
          <TabsTrigger value="inventory" data-testid="wh-tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="movements" data-testid="wh-tab-movements"><History size={14} className="mr-1.5" /> Movements</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory">
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="p-4">SKU</th><th className="p-4">Product</th><th className="p-4 hidden md:table-cell">Warehouse</th><th className="p-4">Bin</th><th className="p-4">Qty</th><th className="p-4">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.length === 0 && <tr><td colSpan={6} className="p-6 text-muted-foreground">No stock yet. Use <b>Put Away</b> to receive inventory.</td></tr>}
                {data.items.map((i) => (
                  <tr key={i.id} data-testid={`wh-item-${i.sku}`} className="hover:bg-muted/40">
                    <td className="p-4 font-mono font-semibold flex items-center gap-2"><ScanLine size={14} className="text-primary" /> {i.sku}</td>
                    <td className="p-4">{i.name}</td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground">{i.warehouse}</td>
                    <td className="p-4 font-mono">{i.location_bin}</td>
                    <td className="p-4 font-semibold">{i.quantity} {i.uom}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${i.status === "Low Stock" ? "bg-orange-100 text-orange-700 dark:bg-orange-500/15" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15"}`}>{i.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="movements">
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="p-4">Time</th><th className="p-4">Type</th><th className="p-4">SKU</th><th className="p-4">Qty</th><th className="p-4 hidden md:table-cell">From → To</th><th className="p-4 hidden lg:table-cell">By</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(moves.data?.movements || []).length === 0 && <tr><td colSpan={6} className="p-6 text-muted-foreground">No movements recorded yet.</td></tr>}
                {(moves.data?.movements || []).map((m) => (
                  <tr key={m.id} data-testid={`wh-move-${m.id}`} className="hover:bg-muted/40">
                    <td className="p-4 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmt(m.timestamp)}</td>
                    <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${TYPE_BADGE[m.type] || "bg-slate-100 text-slate-700"}`}>{m.type}</span></td>
                    <td className="p-4 font-mono font-semibold">{m.sku}</td>
                    <td className="p-4 font-semibold">{m.quantity}</td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground">
                      {m.from_warehouse ? `${m.from_warehouse} (${m.from_bin})` : "—"} <ArrowLeftRight size={12} className="inline mx-1" /> {m.to_warehouse ? `${m.to_warehouse} (${m.to_bin})` : "—"}
                    </td>
                    <td className="p-4 hidden lg:table-cell text-muted-foreground">{m.user_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
